import client from 'client';
import generateId from 'helpers/generate-id';

import { SERVER_URL } from 'constants/index';

const ONE_MB = 1000000
const FIFTEEN_MB = ONE_MB * 15;
const MAX_REQ_IN_QUEUE = 3;


const cancelUpload = function (transferId, uploadId) {
  const url = `${SERVER_URL}/api/v1/transfers/${transferId}/uploads/${uploadId}/cancel`;
  return client.post(url);
}

const finishUpload = function (transferId, uploadId) {
  const url = `${SERVER_URL}/api/v1/transfers/${transferId}/uploads/${uploadId}/finish`;
  return client.post(url);
}

const finishTransfer = function (transferid) {
  const url = `${SERVER_URL}/api/v1/transfers/${transferid}/finish`;
  return client.post(url);
}

const getFileUploadUrl = function (transferId, uploadId, part) {
  const url = `${SERVER_URL}/api/v1/transfers/${transferId}/uploads/${uploadId}/url?n=${part}`;
  return client.get(url).then(function (result) {
    if (result) {
      return result.url
    } else {
      return '';
    }
  });
}

const getMultipartUploadId = function (transferId, name, type, size) {
  const url = `${SERVER_URL}/api/v1/transfers/${transferId}/uploads/start/${encodeURIComponent(name)}`;
  return client.post(
    url,
    JSON.stringify({ content_type: type, content_length: size }),
    {
      headers: { 'Content-Type': 'application/json' }
    }
  )
    .then(function (result) {
      if (result) {
        return result.upload_id
      } else {
        return '';
      }
    });
}

const getTransfersId = function (body) {
  const url = `${SERVER_URL}/api/v1/transfers/start`;

  return client.post(
    url,
    JSON.stringify(body),
    {
      headers: { 'Content-Type': 'application/json' }
    }
  )
    .then(function (result) {
      if (result) {
        return result.id
      } else {
        return '';
      }
    });
}

const uploadFile = function (url, blob, type, { onProgress, getRequest } = {}) {
  return client.put(url, blob, {
    headers: {
      'Content-Type': type
    },
    onProgress,
    getRequest,
    size: blob.size
  })
}

const prepareFiles = (files) => {
  const preparedFiles = [];

  const _files = files.slice();

  let blobsCount;

  for (let i = 0; i < _files.length; i++) {
    const _file = _files[i];
    const file = Object.assign({}, _file);

    const realFile = file.file;
    const fileSize = realFile.size;

    blobsCount = 1;

    if (fileSize > FIFTEEN_MB && realFile.slice) {
      for (let start = 0; start < fileSize; start++) {
        let end = start + FIFTEEN_MB;

        if (end > fileSize) {
          end = fileSize + 1;
        }

        preparedFiles.push({
          blob: realFile.slice(start, end, realFile.type),
          id: generateId(),
          index: blobsCount,
          file: realFile,
          fileId: file.id
        })

        start = end - 1;

        blobsCount++;
      }
    } else {
      preparedFiles.push({
        blob: realFile,
        id: generateId(),
        index: blobsCount,
        file: realFile,
        fileId: file.id
      });
    }

  }

  return preparedFiles;
}

const beginFileUpload = async (transferId, uploadId, fileBlob, updateProgress, getRequest) => {
  const url = await getFileUploadUrl(transferId, uploadId, fileBlob.index);

  await uploadFile(
    url,
    fileBlob.blob,
    fileBlob.blob.type,
    {
      onProgress: function (progress, transferred) {
        fileBlob.progress = progress;
        fileBlob.transferred = transferred;

        if (updateProgress) updateProgress();
      },
      getRequest
    }
  );
}

const verifyFiles = async (transferId, storage) => {
  const files = [].concat(storage.pending, storage.uploading, storage.uploaded);
  
  const fileKeys = {};

  files.forEach(file => fileKeys[file.fileId] = true);

  const fileIds = Object.keys(fileKeys);

  const promises = [];

  for (let i = 0; i < fileIds.length; i++) {
    const fileId = fileIds[i];

    promises.push(finishUpload(transferId, storage.uploadIds[fileId]));
  }

  await Promise.all(promises);
}

const getNextFile = storage => () => {
  if (storage.pending.length > 0) {
    return storage.pending.pop();
  } else {
    return null;
  }
}

const startUpload = async ({ body, files, onProgress }, config, storage) => {
  storage.pending = prepareFiles(files);

  const fetchFile = getNextFile(storage);

  const getRequest = request => {
    storage.requests.push(request)
  };

  const transferId = await getTransfersId(body, { getRequest });

  storage.transferId = transferId;
  storage.uploadIds = {}

  const updateProgress = () => {
    if (onProgress) {
      const total = storage.getTotalBytes()
      onProgress(total);
    }
  }

  let queueSpaceLeft = MAX_REQ_IN_QUEUE;

  let nextFile = fetchFile();

  while (nextFile && !config.stopped) {
    
    const promises = [];

    while (nextFile && queueSpaceLeft > 0) {
      let uploadId = storage.uploadIds[nextFile.fileId];

      if (!uploadId) {
        uploadId = await getMultipartUploadId(
          transferId,
          nextFile.file.name,
          nextFile.file.type,
          nextFile.file.size,
          { getRequest }
        );
        storage.uploadIds[nextFile.fileId] = uploadId;
      }

      storage.uploading.push(nextFile);

      promises.push(
        (async (_nextFile) => {
          await beginFileUpload(transferId, storage.uploadIds[_nextFile.fileId], _nextFile, updateProgress, getRequest);
          storage.uploading = storage.uploading.filter(file => _nextFile.id !== file.id);
          storage.uploaded.push(_nextFile);
  
        })(nextFile)
      );

      nextFile = fetchFile();
      queueSpaceLeft--;
    }

    if ((!nextFile || storage.uploading.length > 0) && !config.stopped) {
      await Promise.all(promises);

      queueSpaceLeft = MAX_REQ_IN_QUEUE;
    }
  }

  if (!config.stopped) {
    await verifyFiles(transferId, storage);

    const result = await finishTransfer(transferId);
    return { link: result.link, transferId };
  } else {
    const e = new Error('');
    e.stopped = true;
    throw e;
  }
}

const stopUpload = (config = {}, storage = {}) => async () => {
  const promises = [];
  const cancelPromises = [];
  config.stopped = true;

  if (storage.requests) {
    storage.requests.forEach(request => {
      promises.push(new Promise(resolve => {
        try {
          request.abort();
        } catch(e) {}

        resolve();
      }))
    })
  }

  await Promise.all(promises);

  const filesToCancel = [].concat(storage.pending, storage.uploading);

  for (let i = 0; i < filesToCancel.length; i++) {
    const file = filesToCancel[i];
    const uploadId = storage.uploadIds[file.fileId];

    if (uploadId) {
      cancelPromises.push(cancelUpload(storage.transferId, uploadId))
    }
  }

  try {
    await Promise.all(cancelPromises);
  } catch(e) {}
}

const uploader = (() => {
  const config = {};
  const storage = {
    finishedFiles: {},
    pending: [],
    transferId: '',
    uploading: [],
    uploaded: [],
    requests: [],
    uploadIds: {},
    reset(){
      this.transferId = '';
      this.finishedFiles = {};
      this.uploadIds = {};
      this.pending = [];
      this.uploading = [];
      this.uploaded = [];
      this.requests = [];
    },
    getTotalBytes() {
      let totalBytes = 0;

      this.uploaded.forEach(file => {
        totalBytes += file.transferred || 0;
      });
      this.uploading.forEach(file => {
        totalBytes += file.transferred || 0;
      });

      return totalBytes;
    }
  }

  return {
    getController: () => ({
      reset() {
        config.stopped = false;
        storage.reset();
      },
      startUpload: input => startUpload(input, config, storage),
      stopUpload: stopUpload(config, storage)
    })
  }
})();

export default uploader;